export const CONSENT_COOKIE_NAME = 'bookeper_consent';
export const CONSENT_COOKIE_DOMAIN = '.bookeper.pl';
export const CONSENT_COOKIE_PATH = '/';
export const CONSENT_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

export type ConsentStatus = 'accepted' | 'declined';

export interface ConsentValue {
  status: ConsentStatus;
  date: string;
}

/**
 * What the cookie holds. `epoch` is the consent epoch: a counter that every withdrawal (a refusal,
 * or clearing the decision) increments, here, in the one cookie every origin on `.bookeper.pl`
 * reads, so a withdrawal made on www outdates what app tabs derived before it. Accepting never
 * changes it. A cookie with no `status` is the tombstone `clearConsent` leaves: no decision, but
 * the epoch survives.
 */
interface StoredConsent extends ConsentValue {
  epoch: number;
}

interface ConsentDocument {
  cookie: string;
}

interface ConsentWindow {
  location?: {
    hostname?: string;
  };
}

function getBrowserDocument(): ConsentDocument | undefined {
  return (globalThis as typeof globalThis & { document?: ConsentDocument }).document;
}

function getBrowserWindow(): ConsentWindow | undefined {
  return (globalThis as typeof globalThis & { window?: ConsentWindow }).window;
}

function isConsentStatus(value: unknown): value is ConsentStatus {
  return value === 'accepted' || value === 'declined';
}

export function getConsentCookieDomain(hostname?: string): string | null {
  const browserWindow = getBrowserWindow();
  if (!browserWindow && !hostname) {
    return CONSENT_COOKIE_DOMAIN;
  }

  const resolvedHostname = hostname ?? browserWindow?.location?.hostname ?? '';
  if (resolvedHostname === 'bookeper.pl' || resolvedHostname.endsWith('.bookeper.pl')) {
    return CONSENT_COOKIE_DOMAIN;
  }

  return null;
}

function findCookieValue(name: string): string | null {
  const browserDocument = getBrowserDocument();
  if (!browserDocument) return null;

  // May throw (SecurityError in a sandboxed frame, blocked storage): callers decide how to fail.
  const cookies = browserDocument.cookie.split(';');
  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    if (!trimmed.startsWith(`${name}=`)) {
      continue;
    }

    return trimmed.slice(name.length + 1);
  }

  return null;
}

function buildConsentValue(status: ConsentStatus, date?: string): ConsentValue {
  return {
    status,
    date: date ?? new Date().toISOString(),
  };
}

function toEpoch(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;
}

function buildCookieString(value: ConsentValue | { epoch: number }, maxAge: number): string {
  const domain = getConsentCookieDomain();
  const parts = [
    `${CONSENT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(value))}`,
    `Max-Age=${maxAge}`,
    `Path=${CONSENT_COOKIE_PATH}`,
    'SameSite=Lax',
    'Secure',
  ];

  if (domain) {
    parts.push(`Domain=${domain}`);
  }

  return parts.join('; ');
}

/**
 * The decision a cookie payload holds, whether its date was STORED in it, and its consent epoch. A legacy
 * payload (the plain string, or JSON without a date) is given a fresh `now()` date when read, and a timestamp
 * that a read minted is no evidence of when the decision was made (#9200 Astra r10): it must not outrank a
 * refusal. A payload with no `status` is the tombstone `clearConsent` leaves: no decision, the epoch stays.
 */
function parseStoredConsent(raw: string): {
  value: ConsentValue | null;
  dated: boolean;
  epoch: number;
} {
  const decoded = decodeURIComponent(raw);

  if (isConsentStatus(decoded)) {
    return {
      value: buildConsentValue(decoded, UNDATED_LEGACY_REFUSAL_DATE),
      dated: false,
      epoch: 0,
    };
  }

  const parsed = JSON.parse(decoded) as {
    status?: unknown;
    date?: unknown;
    epoch?: unknown;
  };
  const epoch = toEpoch(parsed.epoch);

  if (!isConsentStatus(parsed.status)) {
    return { value: null, dated: false, epoch };
  }

  const dated = typeof parsed.date === 'string' && !Number.isNaN(Date.parse(parsed.date));
  return {
    // A record with no date of its own has ONE stable answer on every read: the epoch, older than any dated decision
    // (#9200 Sentry). A date minted at each read made two reads disagree, and every guard that compares dates flip.
    value: buildConsentValue(
      parsed.status,
      dated ? (parsed.date as string) : UNDATED_LEGACY_REFUSAL_DATE
    ),
    dated,
    epoch,
  };
}

/** The cookie's epoch: 0 when there is no cookie, null when the cookie cannot be read at all. */
export function getConsentEpoch(): number | null {
  try {
    const raw = findCookieValue(CONSENT_COOKIE_NAME);
    if (!raw) return 0;
    return parseStoredConsent(raw).epoch;
  } catch {
    return null;
  }
}

/** Dispatched on `window` whenever the consent decision changes, here or in another tab. */
export const CONSENT_STORED_EVENT = 'bookeper:cookie-consent-stored';
const CONSENT_CHANNEL_NAME = 'bookeper-cookie-consent';

/**
 * One id per window, shared by every copy of this module in it. A BroadcastChannel delivers a message to
 * every OTHER channel object in the context, including another copy of this module in the same window; a
 * message that carries this window's own id is this tab's own decision and is not an echo to act on.
 */
const TAB_ID_KEY = '__bookeperConsentTabId';
function tabId(): string {
  const scope = globalThis as unknown as Record<string, string | undefined>;
  return (scope[TAB_ID_KEY] ??=
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`);
}

/**
 * This page's own refusal, kept when the browser blocks cookie writes (or deletes): the decision would
 * otherwise be reread as "undecided" and a pre-consent recorder would go on, or a stale persisted
 * acceptance would outlive a withdrawal. Only a refusal is kept, so a remembered value can never grant
 * anything; the more recent of it and the persisted decision wins (#9200 Astra r3 and r4).
 */
let memoryRefusal: ConsentValue | null = null;
let lastAnnounced = '';
let consentChannel: { postMessage(message: unknown): void; onmessage: unknown } | null = null;

interface ConsentEventWindow {
  dispatchEvent(event: unknown): boolean;
  addEventListener(type: string, listener: () => void): void;
}

function getEventWindow(): ConsentEventWindow | undefined {
  const scope = globalThis as typeof globalThis & { window?: ConsentEventWindow };
  return scope.window && typeof scope.window.dispatchEvent === 'function'
    ? scope.window
    : undefined;
}

function consentSignature(value: ConsentValue | null): string {
  return value ? `${value.status}|${value.date}` : '';
}

function announceConsent(): void {
  lastAnnounced = consentSignature(getConsent());
  const eventWindow = getEventWindow();
  const EventCtor = (globalThis as { CustomEvent?: new (type: string) => unknown }).CustomEvent;
  if (eventWindow && EventCtor) eventWindow.dispatchEvent(new EventCtor(CONSENT_STORED_EVENT));
}

function newerOf(
  persisted: ConsentValue | null,
  remembered: ConsentValue | null
): ConsentValue | null {
  if (!remembered) return persisted;
  if (!persisted) return remembered;
  // Equal dates keep the refusal: the safe side.
  return Date.parse(persisted.date) > Date.parse(remembered.date) ? persisted : remembered;
}

/**
 * Fail closed (#9200 Astra r6, r8). When the browser refuses the cookie read, the decision cannot be
 * known: that is UNKNOWN, not "has not chosen" (which lets a pre-consent recorder send) and not a decision
 * either (nothing may be written back, nothing deleted, no stored key skipped). Telemetry and replay read
 * it as a refusal; `readConsent` lets the one caller that migrates stored keys tell the cases apart.
 */
const UNREADABLE_STORE_REFUSAL: ConsentValue = {
  status: 'declined',
  date: '1970-01-01T00:00:00.000Z',
};

export type ConsentRead =
  { kind: 'decided'; value: ConsentValue } | { kind: 'undecided' } | { kind: 'unknown' };

/**
 * A refusal the cookie could not keep (blocked, silently dropped or deleted by the browser) is also
 * written here, so a reload does not forget it: if a "no" cannot be remembered, nothing may record before
 * a "yes". Refusals only; an acceptance needs the real cookie.
 */
/** Distinct from the app-level tombstone key (apps/frontend cookie-consent.ts): the two stores hold different formats. */
export const REFUSAL_FALLBACK_KEY = 'bookeper_consent_refusal_shared';

function fallbackStores(): Storage[] {
  const stores: Storage[] = [];
  for (const name of ['localStorage', 'sessionStorage'] as const) {
    try {
      const store = (globalThis as unknown as Record<string, Storage | undefined>)[name];
      if (store) stores.push(store);
    } catch {
      // Access itself can throw (blocked storage).
    }
  }
  return stores;
}

/**
 * Every refusal-bearing record main ever wrote in web storage (#9200 Astra r13): a refusal already stored must not be
 * lost by this owner's own key or format. Read as refusals on every surface, never deleted by a read (the old code
 * still reads them), ended by an explicit reset (`clearConsent`) or by a stored decision that is newer. Only a
 * REFUSAL is ever taken from them: an acceptance there grants nothing.
 *   bookeper_consent_refusal       the app tombstone, a bare ISO date (apps/frontend, #9144)
 *   bookeper_cookie_consent        JSON {status, date}, the frontend's pre-cookie store (2026-03)
 *   b2b_analyzer_cookie_consent    JSON {accepted, date}, the analyzer's store (2025-12)
 */
const LEGACY_REFUSAL_KEYS = [
  'bookeper_consent_refusal',
  'bookeper_cookie_consent',
  'b2b_analyzer_cookie_consent',
] as const;

/** Same lifetime as the consent cookie: past it the visitor is asked again. */
const LEGACY_REFUSAL_LIFETIME_MS = CONSENT_COOKIE_MAX_AGE_SECONDS * 1000;
/**
 * A legacy record that stored no date cannot be shown to be old: it counts, and any stored decision outranks it. One second
 * past the epoch: `1970-01-01T00:00:00.000Z` is the unreadable-store refusal.
 */
const UNDATED_LEGACY_REFUSAL_DATE = '1970-01-01T00:00:01.000Z';

interface LegacyRefusal {
  value: ConsentValue;
  /** The record stored no date: it never expires (it cannot be shown to be old) and any stored decision outranks it. */
  undated: boolean;
}

function parseLegacyRefusal(key: string, raw: string): LegacyRefusal | null {
  let date: unknown;
  if (key === 'bookeper_consent_refusal' && !raw.trim().startsWith('{')) {
    date = raw; // the bare ISO date of the app tombstone
  } else {
    const parsed = JSON.parse(raw) as { status?: unknown; accepted?: unknown; date?: unknown };
    const refused =
      key === 'b2b_analyzer_cookie_consent'
        ? parsed.accepted === false
        : parsed.status === 'declined';
    if (!refused) return null;
    date = parsed.date;
  }
  if (typeof date !== 'string' || Number.isNaN(Date.parse(date))) {
    // A bare string that is not a date is not a tombstone; a JSON refusal without a date still refuses.
    return key === 'bookeper_consent_refusal' && typeof date === 'string'
      ? null
      : { value: buildConsentValue('declined', UNDATED_LEGACY_REFUSAL_DATE), undated: true };
  }
  if (Date.now() - Date.parse(date) > LEGACY_REFUSAL_LIFETIME_MS) return null;
  return { value: buildConsentValue('declined', date), undated: false };
}

/** A refusal this owner wrote into its own key, or migrated into it; null when expired or not a refusal. */
function parseOwnerRefusal(raw: string): LegacyRefusal | null {
  const parsed = JSON.parse(raw) as {
    status?: unknown;
    date?: unknown;
    migrated?: unknown;
    undated?: unknown;
  };
  if (parsed.status !== 'declined' || typeof parsed.date !== 'string') return null;
  const undated = parsed.undated === true;
  // A copy migrated from main's keys keeps their lifetime; the owner's own refusals never expire here.
  if (
    parsed.migrated === true &&
    !undated &&
    Date.now() - Date.parse(parsed.date) > LEGACY_REFUSAL_LIFETIME_MS
  ) {
    return null;
  }
  return { value: buildConsentValue('declined', parsed.date), undated };
}

function readFallbackRefusal(): ConsentValue | null {
  let newest: ConsentValue | null = null;
  let newestLegacy: LegacyRefusal | null = null;
  for (const store of fallbackStores()) {
    try {
      const raw = store.getItem(REFUSAL_FALLBACK_KEY);
      const own = raw ? parseOwnerRefusal(raw) : null;
      if (own) newest = newerOf(newest, own.value);
    } catch {
      // Unreadable or corrupt: not a decision.
    }
    for (const key of LEGACY_REFUSAL_KEYS) {
      try {
        const raw = store.getItem(key);
        const legacy = raw ? parseLegacyRefusal(key, raw) : null;
        if (
          legacy &&
          (!newestLegacy || newerOf(newestLegacy.value, legacy.value) === legacy.value)
        ) {
          newestLegacy = legacy;
        }
      } catch {
        // Unreadable or corrupt: not a decision.
      }
    }
  }
  if (newestLegacy) {
    // Migrate forward by WRITING this owner's own key; nothing here deletes the old one. A failed write (quota,
    // blocked) changes nothing: the old record stays and keeps answering. The caller's cleanup of its own copy asks
    // `hasDurableRefusal` first, which reads the copy back.
    if (!newest || Date.parse(newestLegacy.value.date) > Date.parse(newest.date)) {
      writeFallbackRefusal(newestLegacy.value, { migrated: true, undated: newestLegacy.undated });
      mirrorRefusalToCookie(newestLegacy.value, newestLegacy.undated);
    }
    newest = newerOf(newest, newestLegacy.value);
  }
  return newest;
}

/**
 * Leave a refusal this origin migrated out of main's localStorage keys in the SHARED cookie too (#9200 Sentry ycVg6): the
 * other subdomain cannot read this origin's localStorage and would otherwise ask a returning decliner again. Written only
 * when the cookie holds no decision as recent (so once, and never over a newer one), at the refusal's own date, and read
 * back by the same reader every consumer uses; a cookie that does not keep it changes nothing (the owner key still does).
 */
function mirrorRefusalToCookie(value: ConsentValue, undated: boolean): void {
  try {
    const browserDocument = getBrowserDocument();
    if (!browserDocument) return;
    const back = readPersisted();
    if (back?.dated && Date.parse(back.value.date) >= Date.parse(value.date)) return;
    // The refusal keeps its OWN expiry (#9200 Cursor ydA9f): what remains of its 365 days, never a fresh year. One that
    // stored no date cannot be shown to be old and gets the full lifetime; one already past it is not mirrored.
    const remainingMs = undated
      ? LEGACY_REFUSAL_LIFETIME_MS
      : Date.parse(value.date) + LEGACY_REFUSAL_LIFETIME_MS - Date.now();
    const maxAge = Math.min(CONSENT_COOKIE_MAX_AGE_SECONDS, Math.floor(remainingMs / 1000));
    if (maxAge <= 0) return;
    browserDocument.cookie = buildCookieString(
      { ...value, epoch: currentEpoch() } as StoredConsent,
      maxAge
    );
  } catch {
    // Blocked or unwritable: the owner key keeps the refusal on this origin.
  }
}

function writeFallbackRefusal(
  value: ConsentValue,
  options?: { migrated?: boolean; undated?: boolean }
): boolean {
  let kept = false;
  for (const store of fallbackStores()) {
    try {
      // A copy migrated from main's keys keeps their lifetime (365 days from the decision); one whose source stored
      // no date never expires.
      store.setItem(
        REFUSAL_FALLBACK_KEY,
        JSON.stringify(
          options?.migrated
            ? { ...value, migrated: true, ...(options.undated ? { undated: true } : {}) }
            : value
        )
      );
      const back = store.getItem(REFUSAL_FALLBACK_KEY);
      kept = (back !== null && parseOwnerRefusal(back) !== null) || kept;
    } catch {
      // Full or blocked: try the next store.
    }
  }
  return kept;
}

function clearFallbackRefusal(options?: { legacy?: boolean; supersededBy?: string }): void {
  for (const store of fallbackStores()) {
    try {
      if (!options?.supersededBy) {
        store.removeItem(REFUSAL_FALLBACK_KEY);
        // An explicit reset ends everything main wrote.
        if (options?.legacy) for (const key of LEGACY_REFUSAL_KEYS) store.removeItem(key);
        continue;
      }
      // An ACCEPTANCE ends the refusals it is strictly newer than (#9200 Cursor): left behind, they would read as a
      // refusal again the day the consent cookie is gone, and re-seed the owner key. A refusal dated at or after it stays
      // (a tie keeps the refusal). The caller has read the acceptance back from the cookie (`acceptanceHeldByCookie`).
      const at = Date.parse(options.supersededBy);
      const own = (() => {
        const raw = store.getItem(REFUSAL_FALLBACK_KEY);
        return raw ? parseOwnerRefusal(raw) : null;
      })();
      if (!own || Date.parse(own.value.date) < at) store.removeItem(REFUSAL_FALLBACK_KEY);
      for (const key of LEGACY_REFUSAL_KEYS) {
        try {
          const raw = store.getItem(key);
          const legacy = raw ? parseLegacyRefusal(key, raw) : null;
          // Unparseable or expired records are not refusals any more, and go with it.
          if (!legacy || Date.parse(legacy.value.date) < at) store.removeItem(key);
        } catch {
          // Unreadable: left as it is.
        }
      }
    } catch {
      // Nothing to clear.
    }
  }
}

/**
 * Has this acceptance been READ BACK from the consent cookie, dated at or after `date`? Only then may it supersede a
 * refusal kept anywhere else (#9200 Cursor): a cookie write that is blocked or silently dropped, or a cross-tab message
 * this origin cannot persist, proves nothing here, and the refusal stays.
 */
function acceptanceHeldByCookie(date: string): boolean {
  try {
    const back = readPersisted();
    return (
      back?.value.status === 'accepted' &&
      back.dated &&
      Date.parse(back.value.date) >= Date.parse(date)
    );
  } catch {
    return false;
  }
}

/**
 * Does something OTHER than main's legacy keys hold a refusal dated at or after `atLeast`, read back now, and AS
 * PERSISTENT as those keys (#9200 Astra r14, r15)? Persistence rank: the consent cookie and localStorage (kept across
 * tabs and reloads) outrank sessionStorage (one tab) outrank memory (one page). Only the first rank counts: the legacy
 * keys live in localStorage, so a copy that landed only in sessionStorage (localStorage full) is not a replacement, and a
 * fresh tab would read "undecided". A caller about to drop its own copy of a refusal (the app's tombstone, its pre-cookie
 * stores) asks first; a copy whose replacement was never written, or is less persistent, stays.
 */
export function hasDurableRefusal(atLeast: string): boolean {
  const floor = Date.parse(atLeast);
  if (Number.isNaN(floor)) return false;
  try {
    const back = readPersisted();
    if (back?.value.status === 'declined' && back.dated && Date.parse(back.value.date) >= floor) {
      return true;
    }
  } catch {
    // An unreadable cookie holds nothing we can verify.
  }
  try {
    const local = (globalThis as unknown as Record<string, Storage | undefined>).localStorage;
    const raw = local?.getItem(REFUSAL_FALLBACK_KEY);
    const own = raw ? parseOwnerRefusal(raw) : null;
    if (own && Date.parse(own.value.date) >= floor) return true;
  } catch {
    // Unreadable: not durable.
  }
  return false;
}

export function readConsent(): ConsentRead {
  let persisted: PersistedRead | null = null;
  let readable = true;
  try {
    persisted = readPersisted();
  } catch {
    readable = false;
  }
  const refusal = newerOf(memoryRefusal, readFallbackRefusal());
  // A date a legacy read minted never outranks a refusal; it is evidence only when it was stored.
  const value =
    persisted && !persisted.dated && refusal ? refusal : newerOf(persisted?.value ?? null, refusal);
  if (value) return { kind: 'decided', value };
  return readable ? { kind: 'undecided' } : { kind: 'unknown' };
}

export function getConsent(): ConsentValue | null {
  const read = readConsent();
  if (read.kind === 'decided') return read.value;
  return read.kind === 'unknown' ? UNREADABLE_STORE_REFUSAL : null;
}

/**
 * The page's last answer to "can this browser remember a refusal?". Probed once per page at instance or recorder start
 * and again only when something that can change it happens (a decision, a cross-tab message, a failed refusal write);
 * `null` is "not probed yet", which every reader treats as NO (#9200 Cursor ybm4j: a send-time check must not write).
 */
const PROBE_STATE_KEY = Symbol.for('bookeper.consent.refusalKeepable');
type ProbeHolder = Record<symbol, boolean | null | undefined>;
// Kept on globalThis: each package entry (`consent`, `preconsent-telemetry`) is built with its own copy of this module
// (`splitting: false`), and the copy that probes is not the copy the telemetry predicates read (#9200 CodeRabbit ycaXV).
function getRefusalKeepable(): boolean | null {
  return (globalThis as unknown as ProbeHolder)[PROBE_STATE_KEY] ?? null;
}
function setRefusalKeepable(value: boolean | null): void {
  (globalThis as unknown as ProbeHolder)[PROBE_STATE_KEY] = value;
}

/** Forget the probe: the next admission is refused until the page probes again. For the page's own reset and for tests. */
export function forgetRefusalProbe(): void {
  setRefusalKeepable(null);
}

/**
 * The answer the last probe left, with NO device write: what a send-time check (every event, every snapshot) asks.
 * Unknown (no probe yet) is false, so an undecided visitor is not admitted on a guess.
 */
export function refusalKeepableNow(): boolean {
  return getRefusalKeepable() === true;
}

/**
 * Can this browser remember a refusal? PROBES (writes and removes a value) and stores the answer for `refusalKeepableNow`:
 * a cookie that reads back what was written, else a localStorage / sessionStorage that does. Called at instance or
 * recorder start, never per event.
 */
export function canRememberRefusal(): boolean {
  const answer = probeRefusalStore();
  setRefusalKeepable(answer);
  return answer;
}

/**
 * Re-run the probe after something that can change the answer, but ONLY on a page that probed: a page that never did
 * (no pre-consent flag on) has no send-time check that reads one, so a decision adds no device write beyond itself.
 */
function reprobeIfProbed(): void {
  if (getRefusalKeepable() !== null) canRememberRefusal();
}

/** Probe only if this page has no answer yet (a mounted surface that may be the page's first to ask). */
export function ensureRefusalProbe(): boolean {
  return getRefusalKeepable() ?? canRememberRefusal();
}

function probeRefusalStore(): boolean {
  try {
    const doc = getBrowserDocument();
    if (doc) {
      doc.cookie = `${CONSENT_COOKIE_NAME}_probe=1; Path=/; SameSite=Lax; Max-Age=60`;
      const stuck = doc.cookie.includes(`${CONSENT_COOKIE_NAME}_probe=1`);
      doc.cookie = `${CONSENT_COOKIE_NAME}_probe=; Path=/; SameSite=Lax; Max-Age=0`;
      if (stuck) return true;
    }
  } catch {
    // Fall through to the web storage.
  }
  for (const store of fallbackStores()) {
    try {
      store.setItem(`${REFUSAL_FALLBACK_KEY}_probe`, '1');
      const stuck = store.getItem(`${REFUSAL_FALLBACK_KEY}_probe`) === '1';
      store.removeItem(`${REFUSAL_FALLBACK_KEY}_probe`);
      if (stuck) return true;
    } catch {
      // Try the next store.
    }
  }
  return false;
}

interface PersistedRead {
  value: ConsentValue;
  epoch: number;
  /** Whether the cookie STORED the date (false: a legacy payload whose date this read minted). */
  dated: boolean;
}

function readPersisted(): PersistedRead | null {
  const raw = findCookieValue(CONSENT_COOKIE_NAME);
  if (!raw) {
    return null;
  }

  try {
    const normalized = parseStoredConsent(raw);
    if (!normalized.value) {
      return null;
    }
    const decision = normalized.value;

    // Re-encode a payload into the canonical JSON cookie shape, but only one that STORED its date. A legacy
    // payload (plain string, or JSON without a date) is left as it is: rewriting it would store the date
    // this read minted, and the next read would take that for evidence and let it outrank a refusal held
    // in web storage or memory (#9200 Astra r11). A tidy-up write never invents evidence.
    try {
      const browserDocument = getBrowserDocument();
      if (
        browserDocument &&
        normalized.dated &&
        raw !== encodeURIComponent(JSON.stringify({ ...decision, epoch: normalized.epoch }))
      ) {
        browserDocument.cookie = buildCookieString(
          { ...decision, epoch: normalized.epoch } as StoredConsent,
          CONSENT_COOKIE_MAX_AGE_SECONDS
        );
      }
    } catch {
      // The decision was read; only the tidy-up write failed.
    }

    return { value: decision, dated: normalized.dated, epoch: normalized.epoch };
  } catch {
    return null;
  }
}

/**
 * The ONE place a refusal is made durable (#9200 Astra r9), for one made here and for one received from
 * another tab alike: the cookie if it holds the refusal when read back, else the best web storage that
 * does. Without it a tab that only HEARD the refusal would forget it on reload. Returns whether anything
 * durable now holds it.
 */
function persistRefusal(value: ConsentValue): boolean {
  try {
    const back = readPersisted();
    // The cookie READ-BACK holds this refusal (or a newer one): no second copy.
    if (
      back?.value.status === 'declined' &&
      back.dated &&
      Date.parse(back.value.date) >= Date.parse(value.date)
    ) {
      clearFallbackRefusal();
      return true;
    }
    // A STORED acceptance strictly newer than the refusal supersedes it: keeping the refusal would
    // outlive, and then beat, the decision that replaced it. An equal timestamp is not newer (a
    // withdrawal wins ties) and a date a legacy read minted is no evidence at all.
    if (
      back?.value.status === 'accepted' &&
      back.dated &&
      Date.parse(back.value.date) > Date.parse(value.date)
    ) {
      return false;
    }
  } catch {
    // An unreadable cookie holds nothing we can verify.
  }
  return writeFallbackRefusal(value);
}

function currentEpoch(): number {
  return getConsentEpoch() ?? 0;
}

/** Every origin's banner and settings page write the decision here, so a refusal bumps the epoch once, for all of them. */
export function setConsent(status: ConsentStatus, date: string = new Date().toISOString()): void {
  // A date that is not a date cannot order this decision against any other: it is the moment it was made.
  if (Number.isNaN(Date.parse(date))) date = new Date().toISOString();
  const value = buildConsentValue(status, date);
  if (status === 'declined') memoryRefusal = value;
  try {
    const browserDocument = getBrowserDocument();
    if (browserDocument) {
      const epoch = currentEpoch() + (status === 'declined' ? 1 : 0);
      browserDocument.cookie = buildCookieString(
        { ...value, epoch } as StoredConsent,
        CONSENT_COOKIE_MAX_AGE_SECONDS
      );
    }
  } catch {
    // The write failed (SecurityError, blocked storage). The decision still stands in memory and is
    // announced and broadcast below: a refusal must reach every observer.
  }
  if (status === 'accepted') {
    // The newest decision wins, once the cookie has kept it: an acceptance supersedes a refusal kept elsewhere only
    // when it is read back (#9200 Cursor). Otherwise every refusal stays, in memory too, and the next load reads it.
    if (acceptanceHeldByCookie(date)) {
      memoryRefusal = null;
      clearFallbackRefusal({ supersededBy: date });
    }
  } else {
    persistRefusal(value);
  }
  reprobeIfProbed(); // a decision can change what this browser keeps (a refusal write that failed included)
  announceConsent();
  try {
    consentChannel?.postMessage({ status, date, tab: tabId() });
  } catch {
    // A closed channel cannot invalidate the decision made here.
  }
}

/** Clearing the decision is a withdrawal: the decision goes, the epoch moves on and stays. */
export function clearConsent(): void {
  memoryRefusal = null;
  lastAnnounced = '';
  clearFallbackRefusal({ legacy: true });
  try {
    const browserDocument = getBrowserDocument();
    if (browserDocument) {
      browserDocument.cookie = buildCookieString(
        { epoch: currentEpoch() + 1 },
        CONSENT_COOKIE_MAX_AGE_SECONDS
      );
    }
  } catch {
    // Nothing to clear when the store cannot be written.
  }
  // A reset to pending is a change of the stored decision like any other (#9200 Cursor): this window's listeners
  // resync (a pre-consent recorder starts for a visitor who may be recorded again), and the other tabs end the
  // refusals they hold.
  reprobeIfProbed();
  announceConsent();
  try {
    consentChannel?.postMessage({ status: null, cleared: true, tab: tabId() });
  } catch {
    // A closed channel cannot invalidate the reset made here.
  }
}

/**
 * Another tab's decision, adopted here: a tab that cannot read the shared cookie (blocked) learns of a
 * refusal from the channel, and every tab rereads the cookie when it regains focus. Each change is
 * announced through CONSENT_STORED_EVENT, so observers have one signal.
 */
function installCrossTabConsentSync(): void {
  const scope = globalThis as typeof globalThis & {
    __bookeperConsentSync?: boolean;
    document?: {
      addEventListener(type: string, listener: () => void): void;
      visibilityState?: string;
    };
    BroadcastChannel?: new (name: string) => NonNullable<typeof consentChannel>;
  };
  const eventWindow = getEventWindow();
  if (!eventWindow || scope.__bookeperConsentSync) return;
  scope.__bookeperConsentSync = true;

  lastAnnounced = consentSignature(getConsent());
  const reread = () => {
    if (consentSignature(getConsent()) !== lastAnnounced) announceConsent();
  };
  eventWindow.addEventListener('focus', reread);
  eventWindow.addEventListener('pageshow', reread);
  scope.document?.addEventListener('visibilitychange', () => {
    if (scope.document?.visibilityState === 'visible') reread();
  });

  if (typeof scope.BroadcastChannel !== 'function') return;
  consentChannel = new scope.BroadcastChannel(CONSENT_CHANNEL_NAME);
  consentChannel.onmessage = (message: {
    data?: { status?: unknown; date?: unknown; tab?: unknown; cleared?: unknown };
  }) => {
    const { status, date, tab, cleared } = message.data ?? {};
    if (tab === tabId()) return; // this window's own decision
    reprobeIfProbed(); // another tab's decision: the one moment besides our own that can change the answer
    if (cleared === true) {
      // A real reset to pending made in another tab (the app announces it as `cleared`, not as a null a failed
      // write left): the refusals this tab holds end with it.
      memoryRefusal = null;
      clearFallbackRefusal({ legacy: true });
      if (consentSignature(getConsent()) !== lastAnnounced) announceConsent();
      return;
    }
    if (!isConsentStatus(status) || typeof date !== 'string' || Number.isNaN(Date.parse(date)))
      return;
    // The most recent decision wins, by timestamp: a refusal is kept unless a later acceptance
    // supersedes it, and an older message never replaces a newer one (#9200 Cursor). The refusal a
    // reload left in web storage counts as this tab's refusal too.
    const refusal = newerOf(memoryRefusal, readFallbackRefusal());
    if (status === 'declined') {
      if (!refusal || Date.parse(date) >= Date.parse(refusal.date)) {
        memoryRefusal = buildConsentValue(status, date);
        persistRefusal(memoryRefusal); // a refusal heard from another tab is kept like one made here
      }
    } else if (
      refusal &&
      Date.parse(date) > Date.parse(refusal.date) &&
      // The message alone proves nothing for this origin: the cookie (shared across the subdomains) must hold it.
      acceptanceHeldByCookie(date)
    ) {
      memoryRefusal = null;
      clearFallbackRefusal({ supersededBy: date });
    }
    // An unchanged state is not announced again.
    if (consentSignature(getConsent()) !== lastAnnounced) announceConsent();
  };
}

installCrossTabConsentSync();
